// Procedure Prescription.tsx

// IMPORT REACT
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import parse from "html-react-parser";
import ReactQuill, { Quill } from "react-quill";
import { components } from "react-select";
import cloneDeep from "lodash/cloneDeep";
import dayjs, { Dayjs } from "dayjs";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  DialogActions,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { SimpleAsyncSelect } from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
import { selectOption } from "../../../services/models/Context";
import { insertTextAtCursor } from "../../../shared/CommonFunction";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetQuickNotesList } from "../../../services/QuickNotesService";
import { GetInstructionList } from "../../../services/InstructionService";
import DraggableList from "../../../components/Draggable/DraggableList";
import { GetMedicineDoseListForOPD } from "../../../services/MedicineDoseService";
import { useFrequencyUnitList } from "../../../hooks/FrequencyUnit";
import { usePrintUnitList } from "../../../hooks/PrintUnit";
import MedicineSave from "../../Medicine/Medicine/MedicineSave";
import QuickNotesSave from "../../QuickNotes/QuickNotesSave";
import InstructionSave from "../../Instruction/InstructionSave";
import MedicineIndicationForSOSMedicineSave from "../../Medicine/MedicineIndicationforSOSmedicine/MedicineIndicationforSOSmedicineSave";

interface ProcedureVisitMedicineData {
  procedureVisitMedicineId?: number;
  isMedicineNoteEnabled: boolean;
  medicineNote: string;
  isInstructionNoteEnabled: boolean;
  instructionNote: string;
  procedureVisitMedicineDetailColl: MedicineDetail[];
  procedureVisitMedicineInstructionColl: MedicineInstruction[];
}
interface MedicineDetail {
  procedureVisitMedicineDetailId?: number;
  medicineId: number;
  medicineName: string;
  medicineDoseId: number;
  displayOrder: number;
  printUnit?: string;
  routeOfAdministrationId?: number;
  quantityUnit?: string;
  relationToFoodTimeGapId?: number;
  isDeleted: boolean;
  mode: "I" | "U" | "D";
}
interface MedicineInstruction {
  procedureVisitMedicineInstructionId?: number;
  instructionId: number;
  instructionData: string;
  displayOrder: number;
  isDeleted: boolean;
  mode: "I" | "U" | "D";
}

// In Prescription.tsx
interface ProcedurePrescriptionProps {
  isForProcedure: boolean;
  medicineData: ProcedureVisitMedicineData | null;
  onMedicineDataChange: (data: ProcedureVisitMedicineData) => void;
  procedureVisitId?: number | null;
}

// PROCEDURE VISIT MEDICINE DETAIL MEDICINE INSTRUCTION
interface IProcedureVisitMedicineDetailMedicineInstruction {
  procedureVisitMedicineDetailMedicineInstructionId: number;
  procedureVisitMedicineDetailId: number;
  parentDisplayOrder: number;
  displayOrder: number;
  instructionId: number;
  instructionData: string;
  isDeleted: boolean;
  mode: string;
}

// PROCEDURE VISIT MEDICINE DETAIL
interface IProcedureVisitMedicineDetail {
  procedureVisitMedicineDetailId: number;
  displayOrder: number;
  medicineId: { label: string; value: string } | null;
  medicineName: string;
  medicineDoseId: number;
  printUnit: selectOption | null;
  printUnitLabel: string;
  medicineIdLabel: string;
  routeOfAdministrationId: selectOption | null;
  routeOfAdministrationName: string | null;
  quantityUnit: selectOption | null;
  quantityUnitLabel: string | null;
  relationToFoodTimeGapId: selectOption | null;
  relationToFoodTimeGapName: string | null;
  isDeleted: boolean;
  mode: string;
}

// PROCEDURE VISIT MEDICINE INSTRUCTION
interface IProcedureVisitMedicineInstruction {
  procedureVisitMedicineInstructionId: number;
  displayOrder: number;
  instructionId: number;
  instructionData: string;
  isDeleted: boolean;
  mode: string;
}

// PROCEDURE VISIT MEDICINE
interface IProcedureVisitMedicine {
  procedureVisitMedicineId: number;
  procedureVisitId: number;
  procedureId: number;
  specialityId: number;
  departmentId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  procedureVisitMedicineDetailColl: Array<IProcedureVisitMedicineDetail> | null;
  procedureVisitMedicineInstructionColl: Array<IProcedureVisitMedicineInstruction> | null;
  isMedicineNoteEnabled: boolean;
  medicineNote: string;
  isInstructionNoteEnabled: boolean;
  instructionNote: string;
  isDeleted: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  consumerId: "",
  orgId: "",
  branchId: "",
};

const skeletonRowCount = 5;

// SLOT VALIDATION
export const slotValidationFn = (context) => {
  const {
    doseFrequency,
    isMorningScheduleEnabled,
    isAfternoonScheduleEnabled,
    isEveningScheduleEnabled,
    isNightScheduleEnabled,
  } = context.parent;

  if (!doseFrequency?.label) return true;

  const maxAllowed = Math.floor(
    doseFrequency.frequencyConversationValue /
    (doseFrequency.frequencyConversationUnit || 1),
  );

  const selectedCount = [
    isMorningScheduleEnabled,
    isAfternoonScheduleEnabled,
    isEveningScheduleEnabled,
    isNightScheduleEnabled,
  ].filter((val) => val).length;

  if (maxAllowed > 4) {
    return selectedCount === 0;
  }
  // return maxAllowed === selectedCount;
  return selectedCount === 0 || selectedCount === maxAllowed;
};

export default function ProcedurePrescription(
  Props: ProcedurePrescriptionProps,
) {
  // PROPS
  const {
    isForProcedure,
    medicineData,
    onMedicineDataChange,
    procedureVisitId,
  } = Props;

  // DEFAULT SCHEMA VALUE FOR FORM
  const initProcedureVisitMedicine: IProcedureVisitMedicine = {
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    procedureVisitMedicineId: 0,
    procedureVisitId: 0,
    procedureId: 0,
    specialityId: 0,
    departmentId: 0,
    procedureVisitMedicineDetailColl: null,
    procedureVisitMedicineInstructionColl: null,
    isMedicineNoteEnabled: false,
    medicineNote: "",
    isInstructionNoteEnabled: false,
    instructionNote: "",
    isDeleted: false,
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const isSuperAdmin =
    (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
    "superadmin";
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  const theme = useTheme();
  const smd = useMediaQuery(theme.breakpoints.down("sm"));
  const mdd = useMediaQuery(theme.breakpoints.down("md"));

  // REFERENCES
  const prescriptionQuillRef = useRef<ReactQuill | null>(null);
  const generalInstructionQuillRef = useRef<ReactQuill | null>(null);

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [param, setParam] = useState(parameters);
  const [selected, setSelected] =
    useState<IProcedureVisitMedicineDetail | null>(null);
  const [asyncSelectKey, setAsyncSelectKey] = React.useState({
    followUpReasonSearch: 1,
    QuickNoteSearch: 1,
    followUpInstruction: 1,
    MedicineDose: 1,
    GeneralInstruction: 1,
    GeneralInstructionQuickNoteSearch: 1,
    MediicineInstruction: 1,
  });
  const [medicineDoseColl, setMedicineDoseColl] = useState<any[]>([]);
  const [generalInstructionColl, setGeneralInstructionColl] = useState<any[]>(
    [],
  );
  const [generalInstructionQuickNoteColl, setGeneralInstructionQuickNoteColl] =
    useState<any[]>([]);

  const [quickNoteColl, setQuickNoteColl] = useState<any[]>([]);
  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
    value: null,
  });
  const [parentPopUp, setParentPopUp] = React.useState<{
    name: string;
    code: string;
    value: any;
  } | null>(null);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    procedureVisitMedicineId: Yup.number().required(),
    procedureVisitId: Yup.number().required(),
    procedureId: Yup.number().required(),
    specialityId: Yup.number().required(),
    departmentId: Yup.number().required(),
    consumerId: Yup.number().required(),
    orgId: Yup.number().required(),
    branchId: Yup.number().required(),
    isMedicineNoteEnabled: Yup.boolean().required(),
    medicineNote: Yup.string().nullable(),
    isInstructionNoteEnabled: Yup.boolean().required(),
    instructionNote: Yup.string().nullable(),
    procedureVisitMedicineDetailColl: Yup.array()
      .of(
        Yup.object().shape({
          procedureVisitMedicineDetailId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          medicineId: Yup.object()
            .nullable()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .required(),
          medicineName: Yup.string().nullable(),
          medicineDoseId: Yup.number().required(),
          procedureVisitMedicineDetailStepColl: Yup.array().of(
            Yup.object().shape({
              procedureVisitMedicineDetailStepId: Yup.number().required(),
              procedureVisitMedicineDetailId: Yup.number().required(),
              parentDisplayOrder: Yup.number().required(),
              displayOrder: Yup.number().required(),
              isDeleted: Yup.boolean().required(),
              mode: Yup.string().required(),
            }),
          ),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      .required("Procedure Visit Medicine is required")
      .test(
        "at-least-one-active",
        "At least one detail must not be deleted",
        (arr) => {
          if (!arr || arr.length === 0) return false;
          return arr.some(
            (item) => item.mode !== "D" && item.isDeleted === false,
          );
        },
      ),
    procedureVisitMedicineInstructionColl: Yup.array()
      .of(
        Yup.object().shape({
          procedureVisitMedicineInstructionId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          instructionId: Yup.number().required(),
          instructionData: Yup.string().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      .nullable(),
    isDeleted: Yup.boolean(),
  });

  const [medicineAddColl, setMedicineAddColl] = useState<any[]>([]);

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initProcedureVisitMedicine,
  });

  const watchedProcedureVisitMedicineDetailColl =
    useWatch({
      name: "procedureVisitMedicineDetailColl",
      control: methods.control,
    }) || [];

  const filteredProcedureVisitMedicineDetailColl =
    watchedProcedureVisitMedicineDetailColl?.filter(
      (x) => x?.isDeleted === false && x.mode !== "D",
    ) || [];

  const watchedProcedureVisitMedicineInstructionColl = useWatch({
    name: "procedureVisitMedicineInstructionColl",
    control: methods.control,
  });

  const filteredProcedureVisitMedicineInstructionColl =
    watchedProcedureVisitMedicineInstructionColl?.filter(
      (x) => x?.isDeleted === false && x.mode !== "D",
    ) || [];

  // GET LIST OF MEDICINE DOSE
  const fetchMedicineDose = async (searchValue: string, oninit: boolean) => {
    try {
      let medicineDoseColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append(
        "departmentId",
        isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
      );
      params.append(
        "specialityId",
        isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
      );
      const result: any = await GetMedicineDoseListForOPD(params);
      if (result?.data?.data?.medicineDoseColl) {
        medicineDoseColl = result?.data?.data?.medicineDoseColl;
        if (oninit) setMedicineDoseColl(medicineDoseColl);
      }
      return medicineDoseColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER  MEDICINE DOSE
  async function filterMedicineDose(inputValue: string) {
    let result = await fetchMedicineDose(inputValue, false);
    return result;
  }

  //  MEDICINE DOSE LOAD ON SEARCH
  const loadMedicineDoseList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineDose(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET LIST OF GENERAL INSRUCTION
  const fetchGeneralInstruction = async (
    searchValue: string,
    oninit: boolean,
  ) => {
    try {
      let generalInstructionColl = [];
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId?.toString());
      params.append("OrgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append(
        "departmentId",
        isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
      );
      params.append(
        "specialityId",
        isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
      );
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", "5");
      // params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("languageCode", "ENGLISH-IN");
      params.append("instructionTypeCode", "GENERAL_INSTRUCTION");
      params.append("isFor", "OPD");
      params.append("mode", "DROPDOWN");
      const result: any = await GetInstructionList(params);
      if (result?.data?.data?.instructionColl) {
        generalInstructionColl = result?.data?.data?.instructionColl;
        if (oninit) setGeneralInstructionColl(generalInstructionColl);
      }
      return generalInstructionColl;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER  GENERAL INSRUCTION
  async function filterGeneralInstruction(inputValue: string) {
    let result = await fetchGeneralInstruction(inputValue, false);
    return result;
  }

  //  GENERAL INSRUCTION LOAD ON SEARCH
  const loadGeneralInstructionList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterGeneralInstruction(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  //GET LIST OF GENERALINSTRUCTION QUICK NOTE
  const fetchGeneralInstructionQuickNotesList = async (
    searchValue: string,
    oninit: boolean,
  ) => {
    try {
      let params = new URLSearchParams();
      let generalinstructionquickNotesColl = [];
      params.append("ConsumerId", userContext?.consumerId?.toString());
      params.append("OrganizationId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append(
        "DepartmentId",
        isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
      );
      params.append(
        "SpecialityId",
        isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
      );
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("QuickNotesTypeCode", "GENERAL_INSTRUCTION_NOTE");
      params.append("isFor", "OPD");
      params.append("Mode", "DROPDOWN");
      const result: any = await GetQuickNotesList(params);
      if (result?.data?.data?.quickNotesColl) {
        generalinstructionquickNotesColl = result?.data?.data?.quickNotesColl;
        if (oninit)
          setGeneralInstructionQuickNoteColl(
            generalinstructionquickNotesColl || [],
          );
      }
      return generalinstructionquickNotesColl;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  //GET LIST OF QUICK NOTE
  const fetchQuickNotesList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let quickNotesColl = [];
      params.append("ConsumerId", userContext?.consumerId?.toString());
      params.append("OrganizationId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append(
        "DepartmentId",
        isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
      );
      params.append(
        "SpecialityId",
        isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
      );
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("QuickNotesTypeCode", "PRESCRIPTION_NOTE");
      params.append("isFor", "OPD");
      params.append("Mode", "DROPDOWN");
      const result: any = await GetQuickNotesList(params);
      if (result?.data?.data?.quickNotesColl) {
        quickNotesColl = result?.data?.data?.quickNotesColl;
        if (oninit) setQuickNoteColl(quickNotesColl || []);
      }
      return quickNotesColl;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };
  // ON SUBMIT OF FORM
  const handlePrescriptionFormSubmit = (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    // Prevent nested submit events from bubbling to parent Procedure form.
    e.preventDefault();
    e.stopPropagation();
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchMedicineDose("", true);
      await fetchGeneralInstruction("", true);
      await fetchQuickNotesList("", true);
      await fetchGeneralInstructionQuickNotesList("", true);

      // ✅ SIMPLIFIED - medicineData handled by useEffect
      if (!medicineData) {
        methods.reset({
          ...initProcedureVisitMedicine,
          consumerId: userContext.consumerId,
          orgId: userContext.organizationId,
          branchId: userContext.branchId,
          departmentId: userContext.departmentId,
          specialityId: userContext.specialityId,
        });
      }

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  const medicineDoseOptionComponent = (props) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box>
            <Grid container>
              <Grid item xs={12}>
                <Box>{props?.children}</Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography>{props.data?.medicineIdLabel || ""}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </components.Option>
    );
  };

  const medicineInstructionOptionComponent = (props) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box
            dangerouslySetInnerHTML={{
              __html: props?.children,
            }}
          />
        )}
      </components.Option>
    );
  };

  const PrescriptionDetailCard = ({
    item,
  }: {
    item: IProcedureVisitMedicineDetail;
  }) => {
    // ✅ Extract string values from objects
    const printUnitText =
      typeof item?.printUnit === "object"
        ? item?.printUnit?.label
        : item?.printUnitLabel;
    const routeText =
      typeof item?.routeOfAdministrationId === "object"
        ? item?.routeOfAdministrationId?.label
        : item?.routeOfAdministrationName;
    const foodText =
      typeof item?.relationToFoodTimeGapId === "object"
        ? item?.relationToFoodTimeGapId?.label
        : item?.relationToFoodTimeGapName;

    return (
      <Box sx={{ display: "flex", gap: 2, padding: "10px" }}>
        <DragIndicatorIcon sx={{ cursor: "grab" }} />

        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Typography variant="body1" fontWeight={500}>
                {item?.medicineIdLabel || item?.medicineName || ""}
              </Typography>
              {/* {selected?.displayOrder === item.displayOrder && (
                <KeyboardDoubleArrowRightRoundedIcon />
              )} */}
            </Box>
          </Grid>

          {/* Print Unit */}
          {/* {printUnitText && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {printUnitText}
              </Typography>
            </Grid>
          )} */}

          {/* Route of Administration */}
          {/* {routeText && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {routeText}
              </Typography>
            </Grid>
          )} */}

          {/* Relation to Food */}
          {/* {foodText && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {foodText}
              </Typography>
            </Grid>
          )} */}
        </Grid>

        <CloseRoundedIcon
          onClick={() => handleDeletePrescriptionDetail(item)}
          style={{ color: "#d32f2f", cursor: "pointer" }}
        />
      </Box>
    );
  };
  const InstructionDetailCard = ({
    item,
  }: {
    item: IProcedureVisitMedicineInstruction;
  }) => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          padding: "10px",
          alignItems: "center",
        }}
      >
        <DragIndicatorIcon sx={{ cursor: "grab" }} />
        <Grid container>
          <Grid item xs={12}>
            <Box
              dangerouslySetInnerHTML={{
                __html: item.instructionData,
              }}
            />
          </Grid>
        </Grid>
        <CloseRoundedIcon
          onClick={(e) => {
            handleDeleteInstructionDetail(item);
          }}
          style={{ color: "#d32f2f", cursor: "pointer" }}
        />
      </Box>
    );
  };

  // FUNCTIONS
  const handleClickOpenCreatable = (
    value: boolean,
    label: string,
    code: string,
    obj?: any,
  ) => {
    if (
      value &&
      customPopUp.open &&
      customPopUp.code &&
      customPopUp.code !== code
    ) {
      setParentPopUp({
        name: customPopUp.name,
        code: customPopUp.code,
        value: customPopUp.value ?? null,
      });
    }

    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
      value: obj || null,
    });
  };

  const handleCloseCreatable = () => {
    if (customPopUp.code === "MEDICINE" && parentPopUp?.code) {
      setCustomPopUp({
        ...customPopUp,
        open: true,
        name: parentPopUp.name,
        code: parentPopUp.code,
        value: parentPopUp.value ?? null,
      });
      setParentPopUp(null);
      return;
    }

    setCustomPopUp({ ...customPopUp, open: false, name: "", code: "" });
    setParentPopUp(null);
  };

  const handleManagePopup = async (code: string, option: any) => {
    const currentPopUpCode = customPopUp.code;
    const shouldReopenParentPopup =
      currentPopUpCode === "MEDICINE" &&
      parentPopUp?.code === "MULTI_MEDICINE_ADD";

    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "QUICK_NOTES_CREATE") {
      await fetchQuickNotesList("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        QuickNoteSearch: asyncSelectKey.QuickNoteSearch + 1,
      });
    } else if (customPopUp.code === "MEDICINE" && code === "MEDICINE_CREATE") {
      await fetchMedicineDose("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        MedicineDose: asyncSelectKey.MedicineDose + 1,
      });
    } else if (
      code === "QUICK_NOTES_CREATE" &&
      customPopUp.code === "QUICKNOTE"
    ) {
      await fetchQuickNotesList("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        QuickNoteSearch: asyncSelectKey.QuickNoteSearch + 1,
      });
    } else if (
      code === "QUICK_NOTES_CREATE" &&
      customPopUp.code === "GENERAL_INSTRUCTION_QUICKNOTE"
    ) {
      await fetchGeneralInstructionQuickNotesList("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        GeneralInstructionQuickNoteSearch:
          asyncSelectKey.GeneralInstructionQuickNoteSearch + 1,
      });
    } else if (
      code === "INSTUCTION_CREATE" &&
      customPopUp.code === "GENERAL_INSTRUCTION"
    ) {
      handleAddInstructionDetail(option);
      await fetchGeneralInstruction("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        GeneralInstruction: asyncSelectKey.GeneralInstruction + 1,
      });
    }

    if (shouldReopenParentPopup && parentPopUp) {
      setCustomPopUp({
        ...customPopUp,
        open: true,
        name: parentPopUp.name,
        code: parentPopUp.code,
        value: parentPopUp.value ?? null,
      });
      setParentPopUp(null);
    }

    setIsLoading(false);
  };

  const handleAddPrescriptionDetail = (value: any) => {
    const medicines = Array.isArray(value) ? value : [value];
    const detailColl =
      methods.getValues().procedureVisitMedicineDetailColl || [];

    const maxRowNumber = detailColl.reduce(
      (acc, curr) =>
        (curr?.displayOrder || 0) > acc ? curr?.displayOrder : acc,
      0,
    );

    const newRows = medicines.map((med, index) => {
      const newRow: IProcedureVisitMedicineDetail = {
        procedureVisitMedicineDetailId: 0,
        displayOrder: maxRowNumber + 1 + index,
        medicineId: med?.medicineId
          ? {
            label: String(med?.medicineIdLabel || ""),
            value: String(med?.medicineId),
          }
          : null,
        medicineName: String(med?.medicineIdLabel || ""),
        medicineIdLabel: String(med?.medicineIdLabel || ""),
        medicineDoseId: med?.medicineDoseId || 0,
        printUnit: med?.dosePrintUnit
          ? { label: med?.dosePrintUnitName || "", value: med?.dosePrintUnit }
          : null,
        printUnitLabel: med?.dosePrintUnitName || "",
        routeOfAdministrationId: med?.routeOfAdministrationId
          ? {
            label: med?.routeOfAdministrationName || "",
            value: med?.routeOfAdministrationId,
          }
          : null,
        routeOfAdministrationName: med?.routeOfAdministrationName || "",
        quantityUnit: null,
        quantityUnitLabel: null,
        relationToFoodTimeGapId: med?.relationToFoodId
          ? {
            label: med?.relationToFoodName || "",
            value: med?.relationToFoodId,
          }
          : null,
        relationToFoodTimeGapName: med?.relationToFoodName || "",
        isDeleted: false,
        mode: "I",
      };
      return newRow;
    });

    methods.setValue(
      "procedureVisitMedicineDetailColl",
      [...detailColl, ...newRows],
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );

    if (newRows.length > 0) {
      setSelected(newRows[newRows.length - 1]);
    }
  };

  const handleDeletePrescriptionDetail = (
    item: IProcedureVisitMedicineDetail,
  ) => {
    const detailColl =
      methods.getValues().procedureVisitMedicineDetailColl || [];

    const updatedColl = detailColl.map((medicine) => {
      if (medicine.displayOrder === item.displayOrder) {
        return {
          ...medicine,
          mode: medicine.procedureVisitMedicineDetailId > 0 ? "D" : "D",
          isDeleted: true,
        };
      }
      return medicine;
    });

    methods.setValue("procedureVisitMedicineDetailColl", updatedColl, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (selected?.displayOrder === item.displayOrder) {
      setSelected(null);
    }

    // ✅ FIX: Convert objects to primitives before sending to parent
    if (onMedicineDataChange) {
      const formData = methods.getValues();
      const convertedDetails = updatedColl.map((d) => ({
        ...d,
        medicineId:
          typeof d.medicineId === "object"
            ? Number(d.medicineId?.value)
            : Number(d.medicineId),
        printUnit:
          typeof d.printUnit === "object" ? d.printUnit?.value : d.printUnit,
        routeOfAdministrationId:
          typeof d.routeOfAdministrationId === "object"
            ? d.routeOfAdministrationId?.value
              ? Number(d.routeOfAdministrationId.value)
              : null
            : d.routeOfAdministrationId,
        quantityUnit:
          typeof d.quantityUnit === "object"
            ? d.quantityUnit?.value
            : d.quantityUnit,
        relationToFoodTimeGapId:
          typeof d.relationToFoodTimeGapId === "object"
            ? d.relationToFoodTimeGapId?.value
              ? Number(d.relationToFoodTimeGapId.value)
              : null
            : d.relationToFoodTimeGapId,
      }));

      onMedicineDataChange({
        procedureVisitMedicineId: formData.procedureVisitMedicineId,
        isMedicineNoteEnabled: formData.isMedicineNoteEnabled || false,
        medicineNote: formData.medicineNote || "",
        isInstructionNoteEnabled: formData.isInstructionNoteEnabled || false,
        instructionNote: formData.instructionNote || "",
        procedureVisitMedicineDetailColl: convertedDetails,
        procedureVisitMedicineInstructionColl:
          formData.procedureVisitMedicineInstructionColl || [],
      });
    }
  };

  // ADD INSTRUCTION
  const handleAddInstructionDetail = (value: any) => {
    const detailColl =
      methods?.getValues()?.procedureVisitMedicineInstructionColl || [];

    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const newRow: IProcedureVisitMedicineInstruction = {
      procedureVisitMedicineInstructionId: 0,
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      instructionId: value?.instructionId || 0,
      instructionData: value?.instructionData || "",
      isDeleted: false,
      mode: "I",
    };

    methods.setValue(
      "procedureVisitMedicineInstructionColl",
      [...detailColl, newRow],
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  // DELETE INSTRUCTION
  const handleDeleteInstructionDetail = (
    item: IProcedureVisitMedicineInstruction,
  ) => {
    const detailColl =
      methods.getValues().procedureVisitMedicineInstructionColl || [];

    const updatedColl = detailColl?.map((instruction) => {
      if (instruction.displayOrder === item.displayOrder) {
        return { ...instruction, mode: "D", isDeleted: true };
      }
      return instruction; // Return other items unchanged
    });

    methods.setValue("procedureVisitMedicineInstructionColl", updatedColl, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // PRESCRIPTION DETAIL DISPLAY ORDER CHANGE
  const handlePrescriptionDetailChange = (value: any) => {
    const { dragIndex, hoverIndex } = value;
    let selectedItem = null;
    const prescriptionDetailColl =
      methods.getValues().prescriptionTemplateDetailColl || [];

    // Clone the array to avoid direct mutation
    const updatedItems = [...prescriptionDetailColl];

    // Get the item at dragIndex
    const draggedItem = updatedItems[dragIndex];

    // Remove the item from its original position (dragIndex)
    updatedItems.splice(dragIndex, 1);

    // Insert the item at hoverIndex
    updatedItems.splice(hoverIndex, 0, draggedItem);

    // Update the displayOrder of each item in the updated array
    const updatedItemsWithDisplayOrder = updatedItems.map((item, index) => {
      let value: any = item;
      value.displayOrder = index;
      if (value?.prescriptionTemplateDetailStepColl) {
        value.prescriptionTemplateDetailStepColl =
          value?.prescriptionTemplateDetailStepColl?.map((x, xIndex) => {
            x.parentDisplayOrder = index;
            x.displayOrder = xIndex;
            x.prescriptionTemplateDetailId = value.prescriptionTemplateDetailId;
            return x;
          });
        value.prescriptionTemplateDetailMedicineInstructionColl =
          value?.prescriptionTemplateDetailMedicineInstructionColl?.map(
            (x, xIndex) => {
              x.parentDisplayOrder = index;
              x.displayOrder = xIndex;
              x.prescriptionTemplateDetailId =
                value.prescriptionTemplateDetailId;
              return x;
            },
          );
      }
      if (
        value.prescriptionTemplateDetailId ===
        selected?.prescriptionTemplateDetailId
      ) {
        selectedItem = value;
      }
      return value;
    });

    methods.setValue(
      "prescriptionTemplateDetailColl",
      updatedItemsWithDisplayOrder,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
    setSelected(selectedItem);
  };

  // PRESCRIPTION INSTRUCTION DISPLAY ORDER CHANGE
  const handleInstructionChange = (value: any) => {
    const { dragIndex, hoverIndex } = value;
    const procedureVisitMedicineInstructionColl =
      methods.getValues().procedureVisitMedicineInstructionColl || [];

    // Clone the array to avoid direct mutation
    const updatedItems = [...procedureVisitMedicineInstructionColl];

    // Get the item at dragIndex
    const draggedItem = updatedItems[dragIndex];

    // Remove the item from its original position (dragIndex)
    updatedItems.splice(dragIndex, 1);

    // Insert the item at hoverIndex
    updatedItems.splice(hoverIndex, 0, draggedItem);

    // Update the displayOrder of each item in the updated array
    const updatedItemsWithDisplayOrder = updatedItems.map((item, index) => {
      let value: any = item;
      value.displayOrder = index;
      return value;
    });

    methods.setValue(
      "procedureVisitMedicineInstructionColl",
      updatedItemsWithDisplayOrder,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [medicineData]);

  // Initialize form with medicineData
  useEffect(() => {
    if (!medicineData || !isForProcedure) return;

    const transformedDetails = (
      medicineData.procedureVisitMedicineDetailColl || []
    )
      // ✅ FIX: Don't filter out deleted items - keep them for backend processing
      .map((d, idx) => ({
        procedureVisitMedicineDetailId: d.procedureVisitMedicineDetailId || 0,
        displayOrder: idx,
        // ✅ FIX: Convert number to object for form
        medicineId: d.medicineId
          ? { label: d.medicineName || "", value: d.medicineId.toString() }
          : null,
        medicineName: d.medicineName || "",
        medicineIdLabel: d.medicineName || "",
        medicineDoseId: d.medicineDoseId || 0,
        printUnit: d.printUnit
          ? { label: d.printUnit, value: d.printUnit }
          : null,
        printUnitLabel: d.printUnit || "",
        routeOfAdministrationId: d.routeOfAdministrationId
          ? { label: "", value: d.routeOfAdministrationId.toString() }
          : null,
        routeOfAdministrationName: "",
        quantityUnit: d.quantityUnit
          ? { label: d.quantityUnit, value: d.quantityUnit }
          : null,
        quantityUnitLabel: d.quantityUnit || "",
        relationToFoodTimeGapId: d.relationToFoodTimeGapId
          ? { label: "", value: d.relationToFoodTimeGapId.toString() }
          : null,
        relationToFoodTimeGapName: "",
        // Simplified fields for procedures
        isDeleted: d.isDeleted || false,
        mode: d.mode || "U",
      }));

    methods.reset({
      ...initProcedureVisitMedicine,
      procedureVisitMedicineId: medicineData.procedureVisitMedicineId || 0,
      consumerId: userContext.consumerId,
      orgId: userContext.organizationId,
      branchId: userContext.branchId,
      departmentId: userContext.departmentId,
      specialityId: userContext.specialityId,
      procedureVisitId: procedureVisitId || 0,
      isMedicineNoteEnabled: medicineData.isMedicineNoteEnabled || false,
      medicineNote: medicineData.medicineNote || "",
      isInstructionNoteEnabled: medicineData.isInstructionNoteEnabled || false,
      instructionNote: medicineData.instructionNote || "",
      procedureVisitMedicineDetailColl: transformedDetails,
      procedureVisitMedicineInstructionColl: (
        medicineData.procedureVisitMedicineInstructionColl || []
      ).map((i) => ({ ...i })), // Keep all instructions including deleted
    });
  }, [medicineData, isForProcedure]);

  // On form change, call parent callback
  useEffect(() => {
    if (!isForProcedure || !onMedicineDataChange) return;

    const subscription = methods.watch((formData) => {
      const allDetails = (formData.procedureVisitMedicineDetailColl || []).map(
        (d) => ({
          ...d,
          // ✅ FIX: Convert object back to number for parent
          medicineId:
            typeof d.medicineId === "object"
              ? Number(d.medicineId?.value)
              : Number(d.medicineId),
          printUnit:
            typeof d.printUnit === "object" ? d.printUnit?.value : d.printUnit,
          routeOfAdministrationId:
            typeof d.routeOfAdministrationId === "object"
              ? d.routeOfAdministrationId?.value
                ? Number(d.routeOfAdministrationId.value)
                : null
              : d.routeOfAdministrationId,
          quantityUnit:
            typeof d.quantityUnit === "object"
              ? d.quantityUnit?.value
              : d.quantityUnit,
          relationToFoodTimeGapId:
            typeof d.relationToFoodTimeGapId === "object"
              ? d.relationToFoodTimeGapId?.value
                ? Number(d.relationToFoodTimeGapId.value)
                : null
              : d.relationToFoodTimeGapId,
        }),
      );

      const allInstructions = (
        formData.procedureVisitMedicineInstructionColl || []
      ).map((i) => ({
        ...i,
      }));

      onMedicineDataChange({
        procedureVisitMedicineId: medicineData?.procedureVisitMedicineId,
        isMedicineNoteEnabled: formData.isMedicineNoteEnabled || false,
        medicineNote: formData.medicineNote || "",
        isInstructionNoteEnabled: formData.isInstructionNoteEnabled || false,
        instructionNote: formData.instructionNote || "",
        procedureVisitMedicineDetailColl: allDetails,
        procedureVisitMedicineInstructionColl: allInstructions,
      });
    });

    return () => subscription.unsubscribe();
  }, [methods.watch, onMedicineDataChange, isForProcedure]);

  return (
    <Box>
      <Box className={"main-container"}>
        <FormProvider {...methods}>
          <form onSubmit={handlePrescriptionFormSubmit} action="">
            <Grid container gap={2}>
              <Grid container item spacing={2}>
                <Grid item display={{ xs: "none", sm: "grid" }}></Grid>
                <Grid item xs={12}>
                  <Box sx={{ paddingBottom: "16px" }}>
                    <Box
                      sx={{
                        border: "1px solid #0000001f",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box className="inner-title-container">
                        <Box
                          className="template-box"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#e1eaf1",
                            height: "60px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography>Prescription</Typography>
                            <Typography className="font-size-11">
                              {isLoading || isLoading
                                ? "syncing"
                                : isLoading
                                  ? "saved"
                                  : ""}
                            </Typography>
                          </Box>
                        </Box>
                        {isLoading ? (
                          <>
                            <Grid container spacing={0}>
                              {Array(skeletonRowCount)
                                ?.fill(0)
                                ?.map((skeletonRow, index) => {
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      key={"skeleton-row" + index?.toString()}
                                      sx={{
                                        borderBottom: "1px solid #ced4da",
                                      }}
                                    >
                                      <SkeletonWrapper
                                        isLoading={isLoading}
                                        variant="rectangular"
                                        className="skeleton-draggable-item"
                                      >
                                        <span></span>
                                      </SkeletonWrapper>
                                    </Grid>
                                  );
                                })}
                            </Grid>
                          </>
                        ) : (
                          <DraggableList
                            items={
                              filteredProcedureVisitMedicineDetailColl || []
                            }
                            setItem={async (value) => {
                              handlePrescriptionDetailChange(value);
                            }}
                            Styledcard={PrescriptionDetailCard}
                            selected={selected?.displayOrder}
                          />
                        )}
                        <Box
                          sx={{
                            padding: "10px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            display: "flex",
                            flexDirection: "column",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              // maxHeight: "130px",
                              overflowY: "auto",
                            }}
                          >
                            {medicineDoseColl
                              ?.slice(0, 4)
                              ?.map((item, index) => (
                                <Box
                                  className="quick-chip"
                                  key={index}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={async () => {
                                    if (item && !isLoading) {
                                      handleAddPrescriptionDetail(item);
                                    }
                                  }}
                                >
                                  <Typography>
                                    {item.medicineDoseTitle}
                                  </Typography>
                                  <Typography>
                                    {[
                                      item?.medicineFormulationName
                                        ? item.medicineFormulationName + " - "
                                        : "",
                                      item?.medicineBrandName || "",
                                      item?.medicineGenericName &&
                                        item.medicineGenericName !== "0"
                                        ? `(${item.medicineGenericName})`
                                        : "",
                                      // (item?.medicineStrengthValue || 0) >
                                      //   0 ||
                                      // (item?.medicineUnitPerVolumeValue ||
                                      //   0) > 0
                                      //   ? `(${
                                      //       item.medicineStrengthValue
                                      //         ? item.medicineStrengthValue +
                                      //           " " +
                                      //           getLabel(
                                      //             item.medicineStrengthUnit,
                                      //             unitsOfStrength
                                      //           )
                                      //         : ""
                                      //     }${
                                      //       item.medicineUnitPerVolumeValue
                                      //         ? " per " +
                                      //           item.medicineUnitPerVolumeValue +
                                      //           " " +
                                      //           getLabel(
                                      //             item.dosePrintUnit,
                                      //             unitsPerVolume
                                      //           )
                                      //         : ""
                                      //     })`
                                      //   : "",
                                    ]}
                                  </Typography>{" "}
                                </Box>
                              ))}{" "}
                            {medicineDoseColl?.length > 0 && (
                              // <Button
                              //   variant="outlined"
                              //   className="quick-chip-more"
                              //   color="primary"
                              //   disabled={isLoading}
                              //   onMouseDown={(e) => e.preventDefault()}
                              //   onClick={() => {
                              //     const saveDetailColl =
                              //       cloneDeep(medicineDoseColl);
                              //     //setMedicineAddColl([...saveDetailColl]);
                              //     handleClickOpenCreatable(
                              //       true,
                              //       "",
                              //       "MULTI_MEDICINE_ADD"
                              //     );
                              //   }}>
                              //   <AddIcon className="add-icon" />
                              //   <span className="add-icon"> More</span>

                              // </Button>
                              <Button
                                variant="outlined"
                                className="quick-chip-more"
                                color="primary"
                                disabled={isLoading}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  const medicineColl =
                                    cloneDeep(medicineDoseColl);
                                  setMedicineAddColl([...medicineColl]);
                                  handleClickOpenCreatable(
                                    true,
                                    "",
                                    "MULTI_MEDICINE_ADD",
                                  );
                                }}
                              >
                                <AddIcon className="add-icon" />
                                <span className="add-icon"> More</span>
                              </Button>
                            )}
                          </Box>
                          <FormControl fullWidth>
                            <SimpleAsyncSelect
                              placeholder="Search or create Medicine "
                              id={"MedicineDoseSearch"}
                              FieldName={"MedicineDose"}
                              createOptionLabel='Create "Medicine"'
                              key={asyncSelectKey?.MedicineDose?.toString()}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e: any) => {
                                if (e) {
                                  handleAddPrescriptionDetail(e);
                                }
                              }}
                              value={null}
                              onCreateOption={(inputvalue: string) => {
                                handleClickOpenCreatable(
                                  true,
                                  inputvalue,
                                  "MEDICINE",
                                  0,
                                );
                              }}
                              loadOptions={loadMedicineDoseList}
                              CustomOptionComponent={
                                medicineDoseOptionComponent
                              }
                              defaultOptions={medicineDoseColl || []}
                              isSearchable
                            />
                          </FormControl>
                        </Box>
                      </Box>
                    </Box>
                    <Box>
                      <Typography className="danger-color">
                        {
                          methods.formState.errors
                            .prescriptionTemplateDetailColl?.message
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid #0000001f",
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Box className="inner-title-container">
                      <Box
                        className="template-box"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: "#e1eaf1",
                          height: "60px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography>General Instruction / Advise</Typography>
                          <Typography className="font-size-11">
                            {isLoading || isLoading
                              ? "syncing"
                              : isLoading
                                ? "saved"
                                : ""}
                          </Typography>
                        </Box>
                      </Box>
                      {isLoading ? (
                        <>
                          <Grid container spacing={0}>
                            {Array(skeletonRowCount)
                              ?.fill(0)
                              ?.map((skeletonRow, index) => {
                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    key={"skeleton-row" + index?.toString()}
                                    sx={{
                                      borderBottom: "1px solid #ced4da",
                                    }}
                                  >
                                    <SkeletonWrapper
                                      isLoading={isLoading}
                                      variant="rectangular"
                                      className="skeleton-draggable-item"
                                    >
                                      <span></span>
                                    </SkeletonWrapper>
                                  </Grid>
                                );
                              })}
                          </Grid>
                        </>
                      ) : (
                        <DraggableList
                          items={
                            filteredProcedureVisitMedicineInstructionColl || []
                          }
                          setItem={(value) => {
                            handleInstructionChange(value);
                          }}
                          Styledcard={InstructionDetailCard}
                        />
                      )}
                      <Box
                        sx={{
                          padding: "10px",
                          paddingLeft: "20px",
                          paddingRight: "20px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {generalInstructionColl?.map((item, index) => (
                          <Box
                            className="quick-chip"
                            key={index}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={async () => {
                              if (item && !isLoading) {
                                if (item?.__isNew__ === true) {
                                  handleClickOpenCreatable(
                                    true,
                                    item.label,
                                    "GENERAL_INSTRUCTION",
                                  );
                                } else {
                                  handleAddInstructionDetail(item);
                                }
                              }
                            }}
                          >
                            {parse(item?.instructionData)}
                          </Box>
                        ))}
                        <FormControl fullWidth>
                          <SimpleAsyncSelect
                            placeholder="Search or create General Instruction / Advise"
                            id={"GeneralInstruction"}
                            FieldName={"GeneralInstruction"}
                            key={asyncSelectKey?.GeneralInstruction?.toString()}
                            menuPortalTarget={document.querySelector("body")}
                            onCreateOption={(label: string) => {
                              handleClickOpenCreatable(
                                true,
                                label,
                                "GENERAL_INSTRUCTION",
                              );
                            }}
                            handleChange={(e: any) => {
                              if (e) {
                                handleAddInstructionDetail(e);
                              }
                            }}
                            value={null}
                            loadOptions={loadGeneralInstructionList}
                            defaultOptions={generalInstructionColl || []}
                            CustomOptionComponent={
                              medicineInstructionOptionComponent
                            }
                            isSearchable
                          />
                        </FormControl>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </form>
          <Dialog
            className="dialogPage"
            open={customPopUp.open && customPopUp.code === "MEDICINE"}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen={smd}
            PaperProps={{
              style: {
                width: "1200px",
                height: "650px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  {/* Update Medicine */}
                  {(customPopUp?.value ?? 0) > 0
                    ? "Update Medicine"
                    : "Save Medicine "}
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <MedicineSave
                handleClosePopUp={(code: string, company: any) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
                popUpMedicineId={customPopUp.value || 0}
              />
            </DialogContent>
            <Divider />
          </Dialog>
          <Dialog
            className="dialogPage"
            open={customPopUp.open && customPopUp.code === "QUICKNOTE"}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen={smd}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Save Quick Note
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <QuickNotesSave
                handleClosePopUp={(code: string, company: any) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
                quickNoteType="prescription"
              />
            </DialogContent>
          </Dialog>
          <Dialog
            className="dialogPage"
            open={
              customPopUp.open &&
              customPopUp.code === "GENERAL_INSTRUCTION_QUICKNOTE"
            }
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen={smd}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Save Quick Note
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <QuickNotesSave
                handleClosePopUp={(code: string, company: any) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
                quickNoteType="general-instruction"
              />
            </DialogContent>

            <Divider />
          </Dialog>
          <Dialog
            className="dialogPage"
            open={
              customPopUp.open && customPopUp.code === "MEDICINE_INSTRUCTION"
            }
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen
            PaperProps={{
              style: {
                width: "1200px",
                height: "650px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Save Medicine instructions
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <InstructionSave
                handleClosePopUp={(code: string, template: any) => {
                  handleManagePopup(code, template);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
                instructionType={"medicine"}
              />
            </DialogContent>
            <Divider />
          </Dialog>
          <Dialog
            className="dialogPage"
            open={customPopUp.open && customPopUp.code === "INDICATION_FOR_SOS"}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen
            PaperProps={{
              style: {
                width: "600px",
                height: "350px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Save Medicine Indication for SOS
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <MedicineIndicationForSOSMedicineSave
                handleClosePopUp={(code: string, company: any) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
              />
            </DialogContent>
            <Divider />
          </Dialog>
          <Dialog
            className="dialogPage"
            open={
              customPopUp.open && customPopUp.code === "GENERAL_INSTRUCTION"
            }
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen
            PaperProps={{
              style: {
                width: "1200px",
                height: "650px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Save General instructions
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <InstructionSave
                handleClosePopUp={(code: string, company: any) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
                instructionType={"general"}
              />
            </DialogContent>
            <Divider />
          </Dialog>

          <Dialog
            className="dialogPage"
            open={customPopUp.open && customPopUp.code === "MULTI_MEDICINE_ADD"}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            scroll="paper"
            fullScreen={mdd}
            PaperProps={{
              style: {
                width: "1200px",
                // height: "650px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography
                    style={{ fontWeight: 600, marginLeft: 0 }}
                    sx={{ ml: 2, flex: 1 }}
                    variant="h6"
                  >
                    Add Medicine
                  </Typography>
                </Box>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <Grid container spacing={1}>
                {medicineAddColl?.map((item, index) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      key={item?.medicineDoseId + item?.medicineId + index}
                    >
                      <Box
                        className={`quick-chip ${!!item?.isChecked ? "add-more-grid" : ""
                          }`}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                        key={index}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (!!item?.isChecked) {
                            setMedicineAddColl((prevMedicines) =>
                              prevMedicines?.map((m) => {
                                if (
                                  m?.medicineId === item?.medicineId &&
                                  m?.medicineDoseId === item?.medicineDoseId
                                ) {
                                  m.isChecked = false;
                                }
                                return m;
                              }),
                            );
                          } else {
                            setMedicineAddColl((prevMedicines) =>
                              prevMedicines?.map((m) => {
                                if (
                                  m?.medicineId === item?.medicineId &&
                                  m?.medicineDoseId === item?.medicineDoseId
                                ) {
                                  m.isChecked = true;
                                }
                                return m;
                              }),
                            );
                          }
                        }}
                      >
                        <Checkbox checked={!!item?.isChecked} />
                        <Box>
                          <Typography>{item.medicineDoseTitle}</Typography>
                          <Typography>
                            {[
                              item?.medicineFormulationName
                                ? item.medicineFormulationName + " - "
                                : "",
                              item?.medicineBrandName || "",
                              item?.medicineGenericName &&
                                item.medicineGenericName !== "0"
                                ? `(${item.medicineGenericName})`
                                : "",
                            ]}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{ borderTop: "1px solid #ced4da", padding: "12px 24px" }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box>
                    <FormControl fullWidth>
                      <SimpleAsyncSelect
                        placeholder="Search or create Medicine"
                        id={"MedicineDoseSearch"}
                        FieldName={"MedicineDose"}
                        key={asyncSelectKey?.MedicineDose?.toString()}
                        handleChange={(e) => {
                          if (e) {
                            setMedicineAddColl([
                              ...medicineAddColl,
                              {
                                ...e,
                                isChecked: true,
                                medicineIdLabel:
                                  e.medicineBrandName ||
                                  e.medicineGenericName ||
                                  "",
                              },
                            ]);
                          }
                        }}
                        value={null}
                        onCreateOption={(inputvalue) => {
                          handleClickOpenCreatable(
                            true,
                            inputvalue,
                            "MEDICINE",
                            0,
                          );
                        }}
                        loadOptions={loadMedicineDoseList}
                        CustomOptionComponent={medicineDoseOptionComponent}
                        defaultOptions={medicineDoseColl || []}
                        isSearchable
                      />
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography className="bold-600" variant="h6">
                      Selected:{" "}
                      {medicineAddColl?.filter((x) => x?.isChecked === true)
                        ?.length > 0
                        ? medicineAddColl?.filter((x) => x?.isChecked === true)
                          ?.length
                        : " - "}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        type="button"
                        className="primary-button"
                        name="save"
                        onClick={async () => {
                          const selectedMedicines = medicineAddColl?.filter(
                            (x) => x?.isChecked,
                          );
                          if (selectedMedicines?.length > 0) {
                            await handleAddPrescriptionDetail(
                              selectedMedicines,
                            );
                          }
                          handleCloseCreatable();
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        color="primary"
                        variant="outlined"
                        type="button"
                        className="secondary-button"
                        name="cancel"
                        onClick={() => {
                          handleCloseCreatable();
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogActions>
            <Divider />
          </Dialog>
        </FormProvider>
      </Box>
    </Box>
  );
}
